import { useState, useEffect, type ReactNode } from 'react';
import {
  doc, collection, onSnapshot, query, orderBy, serverTimestamp, runTransaction,
} from 'firebase/firestore';
import { getClientDb } from '../firebase/clientApp';
import { useAuth } from '../context/AuthContext';
import { Button, Modal, ModalBanner, FormField } from './ui';
import type { InventoryItem, InventoryStatus, InventoryMemo, InventoryMemoStatus, Entity } from '../types';

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function fmtDay(s?: string) {
  if (!s) return '—';
  return new Date(s + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

// Header facts, same look as Inventory's Item History / Send On Memo panel.
function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-3)', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-1)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 200 }}>{children}</div>
    </div>
  );
}
const Divider = () => <div style={{ width: 1, alignSelf: 'stretch', background: 'var(--border)' }} />;
// Same round2 as Sales.tsx/Purchase.tsx/Inventory.tsx — plain float
// subtraction (e.g. splitting a memo's carat) routinely lands a few bits off
// a clean 2-decimal value, which then compounds every time it's split again.
function round2(n: number) { return Math.round(n * 100) / 100; }
// Same normalization as Purchase.tsx/Sales.tsx/InventoryMerge.tsx — keys the
// shared itemCodes/{codeId}/history ledger.
function normalizeCode(code: string): string {
  return code.trim().toUpperCase().replace(/[^A-Z0-9_-]/g, '_').slice(0, 400);
}
function stripUndef<T>(obj: T): T {
  if (Array.isArray(obj)) return obj.map(v => stripUndef(v)) as unknown as T;
  if (obj !== null && typeof obj === 'object' && (obj as object).constructor === Object) {
    return Object.fromEntries(
      Object.entries(obj as Record<string, unknown>)
        .filter(([, v]) => v !== undefined)
        .map(([k, v]) => [k, stripUndef(v)])
    ) as T;
  }
  return obj;
}

// ── Memo In (Record Return) modal ────────────────────────────────────────────
// Two call shapes:
//  - `item` (Inventory.tsx's row action): the item may carry MULTIPLE active
//    memos at once — different clients each holding part of the same
//    parcel's carat — so this modal fetches them live from
//    inventory/{id}/memos and, if there's more than one, lets the user pick
//    which one is coming back. A legacy doc whose `status` is still
//    'on-memo' (written before per-item memos existed, when a full item
//    flipped straight to 'on-memo') is handled with the old one-shot flow
//    instead — flip straight back to 'in-stock', clear the old fields, no
//    subcollection involved.
//  - `memo` (Memos.tsx's row action): the caller already knows exactly which
//    memo is being returned, so the live-fetch picker is skipped entirely.
interface Props {
  open:      boolean;
  onClose:   () => void;
  item?:     InventoryItem | null;
  memo?:     InventoryMemo | null;
  entities?: Entity[];
}

export default function ReturnModal({ open, onClose, item, memo, entities = [] }: Props) {
  const { user } = useAuth();
  const [activeMemos,   setActiveMemos]   = useState<InventoryMemo[]>([]);
  const [memosLoading,  setMemosLoading]  = useState(false);
  const [selectedMemoId, setSelectedMemoId] = useState('');
  const [returnedDate, setReturnedDate] = useState(todayISO());
  const [reason,       setReason]       = useState('');
  const [returnCarat,  setReturnCarat]  = useState('');
  const [saving,       setSaving]       = useState(false);
  const [error,        setError]        = useState('');

  const isLegacy   = !!item && !memo && item.status === 'on-memo';
  const pickerMode = !!item && !memo && !isLegacy;

  useEffect(() => {
    if (!open) return;
    setReturnedDate(todayISO()); setReason(''); setError(''); setSelectedMemoId('');
    if (!pickerMode || !item) { setActiveMemos([]); return; }
    const db = getClientDb();
    if (!db) return;
    setMemosLoading(true);
    const unsub = onSnapshot(
      query(collection(db, 'inventory', item.id, 'memos'), orderBy('date', 'asc')),
      snap => {
        const all = snap.docs.map(d => ({ id: d.id, ...d.data() } as InventoryMemo));
        const active = all.filter(m => m.status === 'active');
        setActiveMemos(active);
        setSelectedMemoId(prev => prev || (active.length === 1 ? active[0].id : ''));
        setMemosLoading(false);
      },
      () => setMemosLoading(false),
    );
    return unsub;
  }, [open, item?.id, pickerMode]); // eslint-disable-line react-hooks/exhaustive-deps

  function entityName(id?: string) { return id ? (entities.find(e => e.id === id)?.companyName ?? '—') : '—'; }

  const targetMemo: InventoryMemo | null = memo ?? (pickerMode ? activeMemos.find(m => m.id === selectedMemoId) ?? null : null);

  // Defaults the Return Carat field to the memo's full amount every time the
  // resolved target memo changes (a direct `memo` prop on open, or a fresh
  // picker selection) — editable down from there for a partial return.
  useEffect(() => {
    // toFixed(2), not a raw String() — a memo doc split before this rounding
    // fix existed may still carry a few stray decimal places, and the field
    // shouldn't surface that to the user as its starting value.
    setReturnCarat(targetMemo ? round2(targetMemo.carat).toFixed(2) : '');
  }, [targetMemo?.id, targetMemo?.carat]);

  // The stone shown in the header, and the memo whose customer / date it shows.
  const stone = memo ?? targetMemo ?? item ?? null;
  const shownMemo = targetMemo ?? (isLegacy && item
    ? { entityId: item.memoToEntityId, date: item.memoDate } as Partial<InventoryMemo>
    : null);
  const onMemoCt = targetMemo ? targetMemo.carat
    : isLegacy && item ? (item.memoCarat ?? item.carat)
    : activeMemos.reduce((s, m) => s + m.carat, 0);

  const returnCaratNum = parseFloat(returnCarat) || 0;
  const canSave = isLegacy
    ? true
    : !!targetMemo && returnCaratNum > 0 && returnCaratNum <= targetMemo.carat + 1e-9;

  async function handleSave() {
    if (!canSave) { setError('Select which memo is being returned.'); return; }
    setSaving(true); setError('');
    const db = getClientDb();
    if (!db) { setError('Database not connected.'); setSaving(false); return; }

    try {
      if (isLegacy && item) {
        // Old one-shot path — a doc that was fully flipped to 'on-memo'
        // before per-item memos existed. No subcollection involved.
        await runTransaction(db, async tx => {
          const ref = doc(db, 'inventory', item.id);
          const snap = await tx.get(ref);
          if (!snap.exists() || (snap.data() as InventoryItem).status !== 'on-memo') {
            throw new Error('This item changed status. Please reload and try again.');
          }
          const current = snap.data() as InventoryItem;
          tx.update(ref, {
            status: 'in-stock' as InventoryStatus,
            memoToEntityId: null, memoDate: null, memoReturnDate: null,
            memoCarat: null, memoPricePerCt: null, memoAmountUSD: null,
            returnedDate, returnReason: reason.trim() || null,
            updatedAt: serverTimestamp(),
          });
          if (current.code) {
            tx.set(doc(collection(db, 'itemCodes', normalizeCode(current.code), 'history')), stripUndef({
              type: 'memo-in', date: returnedDate, carat: current.memoCarat ?? current.carat,
              entityId: current.memoToEntityId,
              inventoryItemId: item.id, notes: reason.trim() || undefined,
              createdAt: serverTimestamp(), createdBy: user!.id,
            }));
          }
        });
      } else if (targetMemo) {
        const itemId = targetMemo.inventoryItemId;
        await runTransaction(db, async tx => {
          const memoRef = doc(db, 'inventory', itemId, 'memos', targetMemo.id);
          const itemRef = doc(db, 'inventory', itemId);
          const [memoSnap, itemSnap] = await Promise.all([tx.get(memoRef), tx.get(itemRef)]);
          if (!memoSnap.exists() || (memoSnap.data() as InventoryMemo).status !== 'active') {
            throw new Error('This memo changed status. Please reload and try again.');
          }
          if (!itemSnap.exists()) {
            throw new Error('The underlying item no longer exists.');
          }
          const memoData    = memoSnap.data() as InventoryMemo;
          const currentItem = itemSnap.data() as InventoryItem;

          // Re-checked against the live carat, not the possibly-stale value
          // the form loaded with — same reasoning as the status check above.
          // Rather than silently clamping, a shrunk memo (another partial
          // return/sale since this form opened) surfaces as an error, same
          // as the status check does.
          if (returnCaratNum > memoData.carat + 1e-6) {
            throw new Error(`This memo now holds only ${memoData.carat.toFixed(2)}ct — it changed since this form opened. Please reload and try again.`);
          }
          // closeCarat === memoData.carat is a full return (the common
          // case); anything less splits the memo: the original doc shrinks
          // to the remainder and stays 'active' (still on memo, still
          // returnable/sellable later), and a new immutable doc records
          // exactly the slice that came back — same "sold slice" pattern
          // Sales.tsx uses for partial sales of ordinary stock, applied here
          // to returns.
          const closeCarat = round2(Math.max(returnCaratNum, 0));
          const remaining  = round2(memoData.carat - closeCarat);

          if (remaining <= 1e-9) {
            tx.update(memoRef, {
              status: 'returned' as InventoryMemoStatus,
              returnedDate, returnReason: reason.trim() || null,
              updatedAt: serverTimestamp(),
            });
          } else {
            tx.update(memoRef, {
              carat: remaining,
              amountUSD: memoData.pricePerCt != null ? round2(memoData.pricePerCt * remaining) : memoData.amountUSD,
              updatedAt: serverTimestamp(),
            });
            const closedRef = doc(collection(db, 'inventory', itemId, 'memos'));
            tx.set(closedRef, stripUndef({
              ...memoData,
              carat: closeCarat,
              amountUSD: memoData.pricePerCt != null ? round2(memoData.pricePerCt * closeCarat) : memoData.amountUSD,
              status: 'returned' as InventoryMemoStatus,
              returnedDate, returnReason: reason.trim() || null,
              updatedAt: serverTimestamp(),
            }));
          }
          tx.update(itemRef, {
            memoCommittedCarat: Math.max(0, (currentItem.memoCommittedCarat ?? 0) - closeCarat),
            updatedAt: serverTimestamp(),
          });
          if (memoData.code) {
            tx.set(doc(collection(db, 'itemCodes', normalizeCode(memoData.code), 'history')), stripUndef({
              type: 'memo-in', date: returnedDate, carat: closeCarat,
              entityId: memoData.entityId,
              inventoryItemId: itemId, notes: reason.trim() || undefined,
              createdAt: serverTimestamp(), createdBy: user!.id,
            }));
          }
        });
      }
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save.');
    }
    setSaving(false);
  }

  return (
    <Modal
      open={open} onClose={onClose} hideHead size="lg"
      footer={
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
          {error && <div className="msg msg-error" style={{ margin: 0 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button variant="primary" onClick={handleSave} loading={saving} disabled={!canSave}>
              Record Return
            </Button>
          </div>
        </div>
      }
    >
      <ModalBanner title={memo ? entityName(memo.entityId) : 'Record Return'} eyebrow="Record Return" onClose={onClose} />
      {stone && (
        <div style={{
          display: 'flex', alignItems: 'stretch', gap: 14, flexWrap: 'wrap',
          margin: '-6px 0 16px', padding: '9px 14px',
          border: '1px solid var(--border)', borderRadius: 'var(--r-sm)', background: 'var(--surface-2)',
        }}>
          {shownMemo && (<>
            <Fact label="Customer">{entityName(shownMemo.entityId)}</Fact>
            <Divider />
            <Fact label="Memo Date">{fmtDay(shownMemo.date)}</Fact>
            <Divider />
          </>)}
          <Fact label="Parcel">{`${stone.shape} ${[stone.color, stone.clarity].filter(Boolean).join('/')}`}</Fact>
          <Divider />
          <Fact label="Code">
            {stone.code
              ? <span style={{ display: 'inline-block', background: 'var(--primary-light)', color: 'var(--primary)', fontFamily: 'var(--font-num)', fontSize: 11, fontWeight: 700, padding: '1px 7px', borderRadius: 5 }}>{stone.code}</span>
              : <span style={{ opacity: 0.4 }}>—</span>}
          </Fact>
          <Divider />
          <Fact label="On Memo"><span style={{ fontFamily: 'var(--font-num)' }}>{onMemoCt.toFixed(2)} ct</span></Fact>
        </div>
      )}

      {pickerMode && (
        memosLoading ? (
          <div style={{ fontSize: 13, color: 'var(--text-3)', padding: '8px 0' }}>Loading memos…</div>
        ) : activeMemos.length === 0 ? (
          <div style={{ fontSize: 13, color: 'var(--text-3)', padding: '8px 0' }}>
            Nothing of this stone is out on memo.
          </div>
        ) : activeMemos.length > 1 && (
          <FormField label="Customer" required>
            <select value={selectedMemoId} onChange={e => setSelectedMemoId(e.target.value)}>
              <option value="">Select…</option>
              {activeMemos.map(m => (
                <option key={m.id} value={m.id}>
                  {entityName(m.entityId)} · {m.carat.toFixed(2)} ct · {fmtDay(m.date)}
                </option>
              ))}
            </select>
          </FormField>
        )
      )}

      <div className="form-row-2">
        {targetMemo && (
          <FormField label={`Return Carat (max ${targetMemo.carat.toFixed(2)})`} required>
            <input
              type="number" step="0.01" min="0.01" max={targetMemo.carat} className="no-spinner"
              value={returnCarat}
              onChange={e => setReturnCarat(e.target.value)}
            />
          </FormField>
        )}
        <FormField label="Return Date" required>
          <input type="date" value={returnedDate} onChange={e => setReturnedDate(e.target.value)} />
        </FormField>
      </div>
      <FormField label="Return Reason (optional)">
        <textarea value={reason} onChange={e => setReason(e.target.value)} placeholder="Why was this item returned?" style={{ minHeight: 56 }} />
      </FormField>
    </Modal>
  );
}
